import { createAsyncThunk } from '@reduxjs/toolkit'
import { createThunkName } from '../../../../utils/createThunkname'
import { AxiosService } from '../../../../services/api'
import { REQUEST_OPTIONS } from '../../../../constants/request-options'
import { DEALER_MASTER_URLS } from '../../../../configs/api'
import { saveData } from '../../../../utils/downloadFile'
import {
  CancelDealerInsuredDto,
  CountTransactionResponse,
  CreateDealerDto,
  DeleteDealerDto,
  ExportDealerQueryParams,
  ExportDealerTransactionQueryParams,
  GetAllDealerQueryParams,
  GetAllDealerResponse,
  GetAllDealerTransactionQueryParams,
  GetAllDealerTransactionResponse,
  GetCountTransactionQueryParams,
  GetDealerAddressResponse,
  GetDealerColorCodeResponse,
  GetDealerInfoByIdResponse,
  GetDealerStatusResponse,
  GetRemarkResponse,
  GetTransactionCommissionResponse,
  GetTransactionDetailResponse,
  GetTransactionInsuredResponse,
  GetTransactionSummaryResponse,
  GetDealerTaxInvoiceByTransactionIdResponse,
  UpdateDealerAddressDto,
  UpdateDealerColorCodeDto,
  UpdateDealerInfoDto,
  UpdateDealerRemarkDto,
  UpdateStatusDealerDto,
  UpdateTransactionInsuredDto,
  UpdateDealerTransactionTaxInvoiceDto,
  UpdateDealerSentTaxInvoiceDto
} from './types'

const PREFIX = 'dealerMaster'

export const DealerMasterService = {
  getAllDealersAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllDealers'),
    async (queryParams: GetAllDealerQueryParams) => {
      try {
        const result = await AxiosService.get<GetAllDealerResponse>(DEALER_MASTER_URLS.getAllDealers, {
          params: queryParams
        })
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  createDealerAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'createDealer'), async (dto: CreateDealerDto) => {
    try {
      await AxiosService.post(DEALER_MASTER_URLS.createDealer, dto)
    } catch (error) {
      throw error
    }
  }),
  deleteDealerAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'deleteDealer'), async (dto: DeleteDealerDto) => {
    try {
      await AxiosService.post(DEALER_MASTER_URLS.deleteDealer, dto)
    } catch (error) {
      throw error
    }
  }),
  exportDealersAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'exportDealer'),
    async (queryParams: ExportDealerQueryParams) => {
      try {
        const result = await AxiosService.get(DEALER_MASTER_URLS.exportDealers, {
          params: queryParams,
          ...REQUEST_OPTIONS
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  ),
  getDealerInfoByIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getDealerInfoById'),
    async (dealerId: string) => {
      try {
        const result = await AxiosService.get<GetDealerInfoByIdResponse>(DEALER_MASTER_URLS.getDealerInfoById(dealerId))
        return result.data.dealer
      } catch (error) {
        throw error
      }
    }
  ),
  getDealerColorCodeAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getDealerColorCode'),
    async (dealerId: string) => {
      try {
        const result = await AxiosService.get<GetDealerColorCodeResponse>(
          DEALER_MASTER_URLS.getDealerColorCode(dealerId)
        )
        return result.data.dealer
      } catch (error) {
        throw error
      }
    }
  ),
  getDealerAddressAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getDealerAddress'),
    async (dealerId: string) => {
      try {
        const result = await AxiosService.get<GetDealerAddressResponse>(DEALER_MASTER_URLS.getDealerAddress(dealerId))
        return result.data.dealer
      } catch (error) {
        throw error
      }
    }
  ),
  getDealerRemarkAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getDealerRemark'), async (dealerId: string) => {
    try {
      const result = await AxiosService.get<GetRemarkResponse>(DEALER_MASTER_URLS.getDealerRemark(dealerId))
      return result.data.dealer
    } catch (error) {
      throw error
    }
  }),
  getDealerStatusAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getDealerStatus'), async (dealerId: string) => {
    try {
      const result = await AxiosService.get<GetDealerStatusResponse>(DEALER_MASTER_URLS.getDealerStatus(dealerId))
      return result.data.dealer
    } catch (error) {
      throw error
    }
  }),
  updateDealerInfoAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateDealerInfo'),
    async (dto: UpdateDealerInfoDto) => {
      try {
        await AxiosService.put(DEALER_MASTER_URLS.updateDealerInfo, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateDealerColorCodeAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateDealerColorCode'),
    async (dto: UpdateDealerColorCodeDto) => {
      try {
        await AxiosService.put(DEALER_MASTER_URLS.updateDealerColorCode, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateDealerAddressAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateDealerAddress'),
    async (dto: UpdateDealerAddressDto) => {
      try {
        await AxiosService.put(DEALER_MASTER_URLS.updateDealerAddress, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateDealerRemarkAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateDealerRemark'),
    async (dto: UpdateDealerRemarkDto) => {
      try {
        await AxiosService.put(DEALER_MASTER_URLS.updateDealerRemark, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateStatusDealerAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateStatusDealer'),
    async (dto: UpdateStatusDealerDto) => {
      try {
        await AxiosService.put(DEALER_MASTER_URLS.updateStatusDealer, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  countTransactionAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'countTransaction'),
    async (queryParams: GetCountTransactionQueryParams) => {
      try {
        const result = await AxiosService.get<CountTransactionResponse>(DEALER_MASTER_URLS.countTransaction, {
          params: queryParams
        })
        return result.data.dealerTransactions
      } catch (error) {
        throw error
      }
    }
  ),
  getAllDealerTransactionsAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllDealerTransactions'),
    async (queryParams: GetAllDealerTransactionQueryParams) => {
      try {
        const result = await AxiosService.get<GetAllDealerTransactionResponse>(
          DEALER_MASTER_URLS.getAllDealerTransactions,
          { params: queryParams }
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  exportDealerTransactionsAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'exportDealerTransactions'),
    async (queryParams: ExportDealerTransactionQueryParams) => {
      try {
        const result = await AxiosService.get(DEALER_MASTER_URLS.exportDealerTransactions, {
          params: queryParams,
          ...REQUEST_OPTIONS
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  ),
  getTransactionInsuredAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getTransactionInsured'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetTransactionInsuredResponse>(
          DEALER_MASTER_URLS.getTransactionInsured(transactionId)
        )
        return result.data.insured
      } catch (error) {
        throw error
      }
    }
  ),
  getTransactionSummaryAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getTransactionSummary'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetTransactionSummaryResponse>(
          DEALER_MASTER_URLS.getTransactionSummary(transactionId)
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  getTransactionDetailAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getTransactionDetail'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetTransactionDetailResponse>(
          DEALER_MASTER_URLS.getTransactionDetail(transactionId)
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  updateTransactionInsuredAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateTransactionInsured'),
    async (dto: UpdateTransactionInsuredDto) => {
      try {
        await AxiosService.put(DEALER_MASTER_URLS.updateTransactionInsured, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  cancelInsuredAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'cancelInsured'),
    async (dto: CancelDealerInsuredDto) => {
      try {
        await AxiosService.put(DEALER_MASTER_URLS.cancelInsured, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  getTransactionCommissionAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getTransactionCommission'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetTransactionCommissionResponse>(
          DEALER_MASTER_URLS.getTransactionCommission(transactionId)
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  getDealerTransactionTaxInvoiceByTransactionIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getDealerTransactionTaxInvoiceByTransactionId'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetDealerTaxInvoiceByTransactionIdResponse>(
          DEALER_MASTER_URLS.getTransactionTaxInvoiceByTransactionId(transactionId)
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  updateDealerTransactionTaxInvoiceAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateDealerTransactionTaxInvoice'),
    async (dto: UpdateDealerTransactionTaxInvoiceDto) => {
      try {
        await AxiosService.put(DEALER_MASTER_URLS.updateTransactionTaxInvoice, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateDealerSentTaxInvoiceAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateDealerSentTaxInvoice'),
    async (dto: UpdateDealerSentTaxInvoiceDto) => {
      try {
        await AxiosService.put(DEALER_MASTER_URLS.updateSentTaxInvoice, dto)
      } catch (error) {
        throw error
      }
    }
  )
}
